import React, { useEffect, useState } from 'react';
import { Button, Table } from 'flowbite-react';
import DateSlotsSelections from './DateSlotsSelections';
import { AvailabilitiesByDurationAndDate, UserAvailabilityInput } from 'types';
import { allWeekDays, getAllDatesInRange, groupAvailabilityByDurationAndDate } from 'shared-utils';
// import moment from 'moment';
import dayjs from 'dayjs';

type Props = {
	duration: number;
	startDate: Date;
	endDate: Date;
	availabilities: UserAvailabilityInput[];
	setAvailabilities: (availabilities: UserAvailabilityInput[]) => void;
};

const DateSlotsList = ({ duration, startDate, endDate, availabilities, setAvailabilities }: Props) => {
	const [availabilitiesByDurationAndDate, setAvailabilitiesByDurationAndDate] =
		useState<AvailabilitiesByDurationAndDate>({});

	const availabilitiesByDuration = availabilitiesByDurationAndDate[duration] || [];
	let dates = getAllDatesInRange(startDate, endDate);

	const handleOnClearUnbookedSlots = (actualDate: Date) => {
		// filter availability to remove unbooked slots for the day.
		let dateStartTime = dayjs(actualDate).startOf('date');
		let dateEndTime = dayjs(actualDate).endOf('date');

		let unchangedAvailabilities = availabilities.filter(a => {
			if (a.isBooked) return true;
			else if (a.duration !== duration) return true;
			else {
				let availabiltyStartDateTime = dayjs(a.startTime);
				let availabiltyEndDateTime = dayjs(a.endTime);

				return (
					availabiltyStartDateTime.isAfter(dateEndTime) ||
					availabiltyEndDateTime.isBefore(dateStartTime) ||
					availabiltyEndDateTime.isSame(dateStartTime)
				);
			}
		});

		// update
		setAvailabilities(unchangedAvailabilities);
	};

	const handleOnSameAsPreviousDay = (previousDate: Date, currentDate: Date) => {
		const weekday = allWeekDays[currentDate.getDay()];
		const currentDateString = dayjs(currentDate).format('DD-MM-YYYY');
		const previousDateString = dayjs(previousDate).format('DD-MM-YYYY');

		let dateStartTime = dayjs(currentDate).startOf('date');
		let dateEndTime = dayjs(currentDate).endOf('date');

		// unchanged availabilities
		let unchangedAvailabilities = availabilities.filter(a => {
			if (a.isBooked) return true;
			else if (a.duration !== duration) return true;
			else {
				let availabiltyStartDateTime = dayjs(a.startTime);
				let availabiltyEndDateTime = dayjs(a.endTime);

				return (
					availabiltyStartDateTime.isAfter(dateEndTime) ||
					availabiltyEndDateTime.isSame(dateStartTime) ||
					availabiltyEndDateTime.isBefore(dateStartTime)
				);
			}
		});

		// get all previous date Availabilities
		let previousDatesAvailabilities = availabilitiesByDuration[previousDateString] || [];

		// get all current date Availabilities
		let currentDatesAvailabilities = availabilitiesByDuration[currentDateString] || [];

		// current booked availabilities
		let bookedCurrentAvailabilities = currentDatesAvailabilities.filter(a => a.isBooked);

		// createnew availabilities with previous booked info
		let newAvailabilities = previousDatesAvailabilities
			.map(previousAvailability => {
				let newStartTime = dayjs(previousAvailability.startTime).add(1, 'day');
				let newEndTime = dayjs(previousAvailability.endTime).add(1, 'day');

				let selectedSlot = bookedCurrentAvailabilities.find(bookedAvailability =>
					dayjs(bookedAvailability.startTime).isSame(newStartTime),
				);

				if (selectedSlot) {
					return {
						...selectedSlot,
						startTime: newStartTime.toDate(),
						endTime: newEndTime.toDate(),
					};
				} else {
					return {
						day: weekday,
						duration,
						isBooked: false,
						startTime: newStartTime.toDate(),
						endTime: newEndTime.toDate(),
					};
				}
			})
			.filter(a => !a.isBooked);

		// update
		setAvailabilities([...unchangedAvailabilities, ...newAvailabilities]);
	};

	useEffect(() => {
		const availabilitiesByDurationAnsDate = groupAvailabilityByDurationAndDate(availabilities);
		setAvailabilitiesByDurationAndDate(availabilitiesByDurationAnsDate);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [availabilities]);

	return (
		<Table>
			<Table.Head>
				<Table.HeadCell>Date</Table.HeadCell>
				<Table.HeadCell>Slots</Table.HeadCell>
				<Table.HeadCell>Actions</Table.HeadCell>
			</Table.Head>
			<Table.Body className="divide-y">
				{dates.map((date, index) => {
					let dateString = dayjs(date).format('DD-MM-YYYY');
					return (
						<Table.Row key={date.toString()}>
							<Table.Cell>
								<div className="w-32 text-base">{dateString}</div>
							</Table.Cell>
							<Table.Cell>
								<DateSlotsSelections
									duration={duration}
									date={date}
									availabilities={availabilities}
									availabilitiesByDate={availabilitiesByDuration[dateString]}
									setAvailabilities={setAvailabilities}
								/>
							</Table.Cell>
							<Table.Cell className="flex flex-col gap-2">
								<Button color="purplePlaced" size="xs" onClick={() => handleOnClearUnbookedSlots(date)}>
									Clear unbooked slots
								</Button>
								{index > 0 && (
									<Button
										color="purplePlaced"
										size="xs"
										onClick={() => handleOnSameAsPreviousDay(dates[index - 1], date)}
									>
										Same as previous day
									</Button>
								)}
							</Table.Cell>
						</Table.Row>
					);
				})}
			</Table.Body>
		</Table>
	);
};

export default DateSlotsList;
